import { ForwardOutlineIcon } from '@carboncopyco/icons';
import type { IDynamicEditData } from '@carboncopyco/types';
import {
  IDynamicEditType,
  IEditElementType,
  IListEditType,
} from '@carboncopyco/types';
import { TextBase, TextCaption } from '@carboncopyco/ui';
import { generateId } from '@carboncopyco/utils';
import { capitalize } from 'lodash-es';
import { actionToIcon, getActionToIcon } from './helpers';

const polygon = (
  <div className="pb-0.5 pr-1 pl-0.5">
    <ForwardOutlineIcon size={18} />
  </div>
);

export interface EditsMenuProps {
  edits: IDynamicEditData[];
}

export const EditsMenu = ({ edits }: EditsMenuProps) => {
  const globalEdits = edits.filter((editData) => editData?.isGlobal);

  const getElementByEditData = (
    editData: IDynamicEditData,
    editType: IDynamicEditType
  ) => {
    if (editType === IDynamicEditType.style) {
      return <TextBase label="Style updated" />;
    }
    if (editType === IDynamicEditType.image) {
      return <TextBase label="Image replaced" />;
    }
    if (editType === IDynamicEditType.input) {
      return <TextBase label="Text input changed" />;
    }
    if (editType === IDynamicEditType.link) {
      return <TextBase label="Link edited" />;
    }
    if (editData.type === IDynamicEditType.chart) {
      const chartTitle = capitalize(
        editData.element.new.serialized?.charts?.chartType || 'chart'
      );
      return <TextBase label={`${chartTitle} Chart updated`} />;
    }

    if (editData.type === IDynamicEditType.list) {
      const { moveType } = editData.element.new.serialized;
      let action;

      switch (moveType) {
        case IListEditType.up:
        case IListEditType.down:
          action = `Moved item ${moveType}`;
          break;
        case IListEditType.duplicate:
          action = 'Row duplicated';
          break;
        case IListEditType.delete:
          action = 'Row deleted';
          break;
      }
      return <TextBase label={action} />;
    }

    if (editData.type === IDynamicEditType.visibility) {
      const elementType = getElementTypeFromEdit(editData);
      return (
        <TextBase
          label={`${getElementDisplayName(elementType)} ${
            editData.element.new.value === 'true' ? 'hidden' : 'shown'
          }`}
        />
      );
    }

    if (
      editData.type === IDynamicEditType.lock ||
      editData.type === IDynamicEditType.unlock
    ) {
      const elementType = getElementTypeFromEdit(editData);
      const isLocked = editData.type === IDynamicEditType.lock;

      return (
        <TextBase
          label={`${getElementDisplayName(elementType)} ${
            editData.element.new.value === 'true' ?? isLocked
              ? 'interactions were disabled'
              : 'interactions were enabled'
          }`}
        />
      );
    }

    if (editData.type === IDynamicEditType.delete) {
      const elementType = getElementTypeFromEdit(editData);
      return (
        <TextBase
          label={`${getElementDisplayName(elementType)} ${
            editData.element.new.value === 'true' ? 'deleted' : 'restored'
          }`}
        />
      );
    }

    const oldValue = editData.element.old.value;
    const newValue = editData.element.new.value;
    return (
      <div className="flex items-center">
        <TextBase
          ellipsis
          label={oldValue}
        />
        {polygon}
        <TextBase
          ellipsis
          label={newValue}
        />
      </div>
    );
  };

  const getElementTypeFromEdit = (
    editData: IDynamicEditData
  ): IEditElementType =>
    editData.element.new.serialized?.elementType || IEditElementType.container;

  const getElementDisplayName = (editType: IEditElementType): string => {
    return editType === IEditElementType.container ? 'Element' : editType;
  };

  const renderItemList = (editData: IDynamicEditData) => {
    const editType = editData.type;
    const action = getActionToIcon(editData);
    const Icon = actionToIcon[action];
    return (
      <li
        key={generateId()}
        className="p-2 flex items-center hover:bg-primary-100 cursor-default group"
      >
        <span>{Icon}</span>
        <span className="mx-4 flex-1 truncate">
          {getElementByEditData(editData, editType)}
        </span>
      </li>
    );
  };

  return (
    <div className="w-full">
      {edits.length > 0 && (
        <div className="pt-2">
          <div className="pl-2">
            <TextCaption
              uppercase
              label="On this page"
              color="text-ds-natural-600"
            />
          </div>
          <ul className="pt-4">{edits.map(renderItemList)}</ul>
        </div>
      )}
      {!!globalEdits.length && (
        <div className="pt-7">
          <div className="pl-2">
            <TextCaption
              uppercase
              label="Global changes"
              color="text-ds-natural-600"
            />
          </div>
          <ul className="pt-4">{globalEdits.map(renderItemList)}</ul>
        </div>
      )}
    </div>
  );
};
